import React, { forwardRef } from 'react'

import { Xmark } from '@gravity-ui/icons'
import { cn, DateField, DateRangePicker as HeroDateRangePicker, I18nProvider, Label, RangeCalendar } from '@heroui/react'
import { CalendarDate, parseDate } from '@internationalized/date'

import { useTimesheetStore } from '@/store/timesheet.store'

type DateRangeValue = {
  start: string;
  end: string;
} | null;

type TimesheetDateRangePickerProps = {
  label?: string;
  value?: DateRangeValue;
  onChange?: (val: DateRangeValue) => void;
  className?: string;
  variant?: 'primary' | 'secondary' | 'ghost';
  name?: string;
  isRequired?: boolean;
  isDisabled?: boolean;
  isInvalid?: boolean;
  onBlur?: React.FocusEventHandler;
  minValue?: string;
  maxValue?: string;
  placeholderValue?: string;
  disableNavigation?: boolean;
};

export const TimesheetDateRangePicker = forwardRef<HTMLDivElement, TimesheetDateRangePickerProps>(({
  label,
  value,
  onChange,
  className,
  variant = 'secondary',
  name,
  isRequired,
  isDisabled,
  isInvalid,
  onBlur,
  minValue,
  maxValue,
  placeholderValue,
  disableNavigation = false,
}, ref) => {
  const { filters, expenses, report } = useTimesheetStore()

  type ExpenseType = 'none' | 'by' | 'other' | 'both' | null

  // Helper to determine the type of expenses for a given day
  const getExpenseType = (dateObj: CalendarDate): ExpenseType => {
    if (!filters.driverID) return null

    const targetDateStr = `${dateObj.year}-${String(dateObj.month).padStart(2, '0')}-${String(dateObj.day).padStart(2, '0')}`
    const dayExpenses = expenses.filter(exp => exp.date?.startsWith(targetDateStr))

    if (dayExpenses.length === 0) return 'none'

    const hasBy = dayExpenses.some(exp => exp.country.toLowerCase() === 'by')
    const hasOther = dayExpenses.some(exp => exp.country.toLowerCase() !== 'by')
    if (hasBy && hasOther) return 'both'
    return hasOther ? 'other' : 'by'
  }

  const isDateInActivity = (dateObj: CalendarDate): boolean => {
    if (!report?.Items) return false
    const targetDateStr = `${dateObj.year}-${String(dateObj.month).padStart(2, '0')}-${String(dateObj.day).padStart(2, '0')}`

    return report.Items.some(item => {
      if (!item.StartDate || !item.EndDate) return false
      const start = item.StartDate.split('T')[0]
      const end = item.EndDate.split('T')[0]
      return targetDateStr >= start && targetDateStr <= end
    })
  }

  const getIndicatorColor = (type: ExpenseType) => {
    if (type === 'none') return 'bg-gray-300'
    if (type === 'by') return 'bg-success'
    if (type === 'other') return 'bg-primary'
    if (type === 'both') return 'bg-purple-500'
    return 'bg-gray-300'
  }

  const minDate = React.useMemo(() => {
    if (!minValue) return undefined
    try {
      return parseDate(minValue)
    } catch {
      return undefined
    }
  }, [minValue])

  const maxDate = React.useMemo(() => {
    if (!maxValue) return undefined
    try {
      return parseDate(maxValue)
    } catch {
      return undefined
    }
  }, [maxValue])

  const placeholderDate = React.useMemo(() => {
    if (!placeholderValue) return undefined
    try {
      return parseDate(placeholderValue)
    } catch {
      return undefined
    }
  }, [placeholderValue])

  // Convert string (YYYY-MM-DD) range to CalendarDate range
  const rangeValue = React.useMemo(() => {
    if (!value?.start || !value.end) return null
    try {
      return {
        start: parseDate(value.start),
        end: parseDate(value.end),
      }
    } catch {
      return null
    }
  }, [value])

  const handleChange = React.useCallback((range: any) => {
    if (typeof onChange === 'function') {
      if (range?.start && range.end) {
        onChange({
          start: range.start.toString(),
          end: range.end.toString(),
        })
      } else {
        onChange(null)
      }
    }
  }, [onChange])

  const handleClear = React.useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    if (typeof onChange === 'function') {
      onChange(null)
    }
  }, [onChange])

  return (
    <I18nProvider locale={'ru-RU'}>
      <HeroDateRangePicker
        ref={ref}
        className={cn('w-full', className as any)}
        value={rangeValue}
        onChange={handleChange}
        isRequired={isRequired}
        isDisabled={isDisabled}
        isInvalid={isInvalid}
        onBlur={onBlur as any}
        minValue={minDate}
        maxValue={maxDate}
        placeholderValue={placeholderDate}
        {...({ name } as any)}
      >
        {label && <Label>{label}</Label>}

        <DateField.Group fullWidth variant={variant as any}>
          <DateField.Input slot="start">
            {segment => <DateField.Segment segment={segment} />}
          </DateField.Input>
          <HeroDateRangePicker.RangeSeparator />
          <DateField.Input slot="end">
            {segment => <DateField.Segment segment={segment} />}
          </DateField.Input>
          <DateField.Suffix>
            {value?.start && value.end && !isDisabled && (
              <button
                type="button"
                onClick={handleClear}
                className="cursor-pointer text-zinc-400 hover:text-zinc-600 transition-colors p-0.5 rounded-full hover:bg-zinc-100 dark:hover:bg-zinc-800 mr-1 flex items-center justify-center"
                aria-label="Clear date range"
              >
                <Xmark className="size-3.5" />
              </button>
            )}
            <HeroDateRangePicker.Trigger>
              <HeroDateRangePicker.TriggerIndicator />
            </HeroDateRangePicker.Trigger>
          </DateField.Suffix>
        </DateField.Group>

        <HeroDateRangePicker.Popover>
          <RangeCalendar aria-label={label ?? 'Date range'}>
            <RangeCalendar.Header>
              <RangeCalendar.YearPickerTrigger
                className={disableNavigation ? "pointer-events-none hover:bg-transparent cursor-default bg-transparent shadow-none" : undefined}
              >
                <RangeCalendar.YearPickerTriggerHeading />
                {!disableNavigation && <RangeCalendar.YearPickerTriggerIndicator />}
              </RangeCalendar.YearPickerTrigger>
              {!disableNavigation && <RangeCalendar.NavButton slot="previous" />}
              {!disableNavigation && <RangeCalendar.NavButton slot="next" />}
            </RangeCalendar.Header>
            <RangeCalendar.Grid>
              <RangeCalendar.GridHeader>
                {day => <RangeCalendar.HeaderCell>{day}</RangeCalendar.HeaderCell>}
              </RangeCalendar.GridHeader>
              <RangeCalendar.GridBody>
                {date => {
                  const expenseType = getExpenseType(date)
                  const indicatorColor = getIndicatorColor(expenseType)
                  const hasActivity = isDateInActivity(date)

                  return (
                    <RangeCalendar.Cell date={date}>
                      {({ formattedDate, isSelected }) => (
                        <div className={cn(
                          "relative flex items-center justify-center w-full h-full rounded-md transition-colors",
                          hasActivity && !isSelected ? "bg-primary/20 dark:bg-primary/30" : ""
                        )}
                        >
                          {formattedDate}
                          {!isSelected && expenseType !== null && (
                            <div className={`absolute bottom-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full ${indicatorColor}`} />
                          )}
                        </div>
                      )}
                    </RangeCalendar.Cell>
                  )
                }}
              </RangeCalendar.GridBody>
            </RangeCalendar.Grid>
            <RangeCalendar.YearPickerGrid>
              <RangeCalendar.YearPickerGridBody>
                {({ year }) => <RangeCalendar.YearPickerCell year={year} />}
              </RangeCalendar.YearPickerGridBody>
            </RangeCalendar.YearPickerGrid>
          </RangeCalendar>
        </HeroDateRangePicker.Popover>
      </HeroDateRangePicker>
    </I18nProvider>
  )
})

// eslint-disable-next-line better-mutation/no-mutation
TimesheetDateRangePicker.displayName = 'TimesheetDateRangePicker'
